import { ArrayHelpers } from 'formik';
import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { BiAt, BiNote, BiPhone, BiUser } from 'react-icons/bi';
import { Contact } from '../../../../../common/src/entities';
import { useEntite } from '../../../Utils/api';
import { useAlertDialog } from '../../../Utils/dialogContext';
import ChampMultiple, { affichageElementDefaut } from '../../Common/ChampMultiple';
import PopoverSimple from '../../Common/PopoverSimple';
import { CopierPressePapier } from '../Utils/CopierPressePapier';
import { PopoutEntite } from '../Utils/PopoutEntite';
import CreerContact from './CreerContact';
import LoadingScreen from '../../Common/LoadingScreen';

interface AjoutProps {
    helpers: ArrayHelpers;
    values: Contact[];
    avecUnBouton?: boolean;
    title?: string;
    maxSize?: number;
    appendedChildren?: ReactElement;
}

/**
 * Affichage d'un bouton qui suit un champ de Contact.
 * @param props
 * @returns html d'un bouton
 */
function AffichageAjout(props: AjoutProps): ReactElement {
    const bsAlert = useAlertDialog();
    return (
        <PopoutEntite
            titre={`Ajouter un ${props.title?.toLowerCase() ?? 'contact'}`}
            entite={Contact}
            isCreatable
            modalSize="lg"
            modalBody={(props) => <CreerContact {...props} />}
            size="sm"
            disabled={props.maxSize === props.values.length}
            onChange={(value) => {
                const contact = value as Contact;
                if (contact) {
                    if (props.values?.find((item) => item.id === contact.id)) {
                        bsAlert('Ce contact est déja associé!', 'warning');
                    } else if (!props.maxSize || props.maxSize > props.values.length) {
                        props.helpers.push(value);
                    }
                }
            }}
            avecUnBouton={props.avecUnBouton}
            appendedChildren={props.appendedChildren}
        />
    );
}

function AffichageTexteFromEntite(props: { value: Contact }): ReactElement {
    const { value } = props;
    let contactValue: Contact | undefined;

    contactValue = value as Contact;

    if (!contactValue) {
        return <></>;
    }

    return <AffichageTexteContenu contactInfo={contactValue} />;
}

function AffichageTexteFromId(props: { value: number }): ReactElement {
    const { value } = props;
    const [contactState, setContactState] = useState<{ data: Contact | undefined; loading: boolean }>({
        data: undefined,
        loading: false,
    });
    const contactId: number = value as number;
    const mountedRef = useRef(true);
    const contactValue: Contact | undefined = contactState.data;

    const { data } = useEntite(Contact, +contactId);
    useEffect(() => {
        if (!contactValue) {
            setContactState({ data: data, loading: false });
        }
        return () => {
            mountedRef.current = false;
        };
    }, [data, setContactState]);

    if (!contactValue) {
        return <>{<LoadingScreen />}</>;
    }

    return <AffichageTexteContenu contactInfo={contactValue} />;
}

function AffichageTexteContenu(props: {
    contactInfo: {
        name: string;
        password_hash: string;
        updatePassword: boolean;
        telephone: string;
        email: string;
        notes: string;
    };
}): ReactElement {
    const { name, password_hash, updatePassword, telephone, email, notes } = props.contactInfo;

    let couleurCompte: string;
    let statusCompte: string;
    if (password_hash && updatePassword) {
        couleurCompte = 'gold';
        statusCompte = 'Mot de passe à changer';
    } else if (password_hash && !updatePassword) {
        couleurCompte = 'green';
        statusCompte = 'Compte créé';
    } else {
        couleurCompte = 'red';
        statusCompte = 'Pas de compte';
    }

    const compteIcone = (
        <PopoverSimple texte={statusCompte}>
            <BiUser color={couleurCompte}></BiUser>
        </PopoverSimple>
    );

    const telephoneIcone = (
        <CopierPressePapier texte={telephone}>
            <PopoverSimple texte={telephone} titre="Téléphone">
                <BiPhone />
            </PopoverSimple>
        </CopierPressePapier>
    );

    const emailIcone = (
        <CopierPressePapier texte={email}>
            <PopoverSimple texte={email} titre="Courriel">
                <BiAt />
            </PopoverSimple>
        </CopierPressePapier>
    );

    const notesIcone = (
        <CopierPressePapier texte={notes}>
            <PopoverSimple texte={notes} titre="Notes">
                <BiNote />
            </PopoverSimple>
        </CopierPressePapier>
    );

    return (
        <span>
            {name} {telephone && telephoneIcone} {email && emailIcone} {notes && notesIcone}{' '}
            {password_hash && compteIcone}
        </span>
    );
}

interface Props {
    id: string;
    avecUnBouton?: boolean;
    labelCol?: number;
    maxSize?: number;
    title?: string;
    appendedChildren?: ReactElement;
    errorMessage?: string;
}

/**
 * Champ non éditable qui contient des élément Contact. On peut
 * ajouter des Contact en appuyant sur un bouton qui suit le champs
 * @param props
 * @returns html du champ et du bouton
 */
export default function ChampMultipleContact(props: Props): ReactElement {
    const affichageAjout = (values, helpers, maxSize, title, avecUnBouton, appendedChildren) => (
        <AffichageAjout
            helpers={helpers}
            values={values}
            maxSize={maxSize}
            title={title}
            avecUnBouton={avecUnBouton}
            appendedChildren={appendedChildren}
        />
    );

    const affichageTexte = (value: Contact | number) =>
        (value as Contact)?.id ? (
            <AffichageTexteFromEntite value={value as Contact} />
        ) : (
            <AffichageTexteFromId value={value as number} />
        );

    return (
        <ChampMultiple
            entite={Contact}
            titre={`${props.title ?? 'Contact'}`}
            {...props}
            affichageAjout={affichageAjout}
            affichageTexte={affichageTexte}
            affichageElement={affichageElementDefaut(Contact)}
        />
    );
}
